/**
 * Yard task transition eligibility rules.
 *
 * Satellite owns all eligibility logic for yard task status transitions.
 * Helios menu, drawer, and DnD must only call `canTransitionYardTaskTo`,
 * `getYardTaskTransitionFeedbackReason`, `getYardTaskTransitionTargets`, or
 * `canTransitionYardTask` — never embed transition rules in UI components.
 */
import { YardTaskStatus } from '@nova/core';

import { isCheckTask, isMoveTask, isTaskBlocked } from './yardTaskPredicates';

import type { Maybe, MoveTaskViewData, TaskViewData } from '@nova/core';

type YardTaskTransitionRequirement = (task: TaskViewData) => boolean;

type YardTaskTransitionGraph = Partial<
  Record<YardTaskStatus, Partial<Record<YardTaskStatus, YardTaskTransitionRequirement[]>>>
>;

export enum YardTaskCompleteBlockReason {
  MissingAssetContainer = 'missingAssetContainer',
  MissingOriginSpot = 'missingOriginSpot',
  MissingDestinationSpot = 'missingDestinationSpot',
  Blocked = 'blocked'
}

/**
 * The `blocked` tag never reflects two of the five OD-4117 conditions (nothing reconciles it for
 * them — see `getYardTaskBlockReason` on the backend); `blockDetail` is resolved live and catches
 * those too, so either signal means blocked.
 */
function hasLiveBlockReason(task: TaskViewData): boolean {
  return Boolean(task.blockDetail?.reason);
}

/**
 * Single OR of both blocked signals, so a caller can't check one and forget the other. Only
 * meaningful for move tasks — `hasLiveBlockReason` is always false for a check task, whose own
 * blocking uses the tag alone (`isNotBlocked`, below), since blocked never gates a check task's
 * Start, only its Complete.
 */
function isBlocked(task: TaskViewData): boolean {
  return isTaskBlocked(task) || hasLiveBlockReason(task);
}

export function getYardTaskCompleteBlockReason(
  task: TaskViewData
): YardTaskCompleteBlockReason | null {
  if (isBlocked(task)) {
    return YardTaskCompleteBlockReason.Blocked;
  }

  if (!isMoveTask(task)) {
    return null;
  }

  const moveTask = task as MoveTaskViewData;

  if (!moveTask.assetContainer?.id) {
    return YardTaskCompleteBlockReason.MissingAssetContainer;
  }

  if (!moveTask.originSpot?.id) {
    return YardTaskCompleteBlockReason.MissingOriginSpot;
  }

  if (!moveTask.destinationSpot?.id) {
    return YardTaskCompleteBlockReason.MissingDestinationSpot;
  }

  return null;
}

const hasMoveCompleteFields: YardTaskTransitionRequirement = task =>
  isMoveTask(task) && getYardTaskCompleteBlockReason(task) === null;

// Move tasks check both blocked signals as one requirement (isBlocked), not two separate ones —
// listing them separately let a future rule addition keep one and drop the other by mistake.
const isNotBlockedForMove: YardTaskTransitionRequirement = task => !isBlocked(task);

// Check tasks intentionally check only the tag: blocked never gated a check task's Start, only its
// Complete, and hasLiveBlockReason is always false for them anyway (see isBlocked above).
const isNotBlocked: YardTaskTransitionRequirement = task => !isTaskBlocked(task);

const YARD_MOVE_TASK_TRANSITION_RULES: YardTaskTransitionGraph = {
  [YardTaskStatus.CREATED]: {
    [YardTaskStatus.CANCELLED]: [isMoveTask],
    [YardTaskStatus.IN_PROGRESS]: [isMoveTask, isNotBlockedForMove],
    [YardTaskStatus.COMPLETED]: [isMoveTask, isNotBlockedForMove, hasMoveCompleteFields]
  },
  [YardTaskStatus.IN_PROGRESS]: {
    [YardTaskStatus.CANCELLED]: [isMoveTask],
    [YardTaskStatus.COMPLETED]: [isMoveTask, isNotBlockedForMove, hasMoveCompleteFields]
  }
};

const YARD_CHECK_TASK_TRANSITION_RULES: YardTaskTransitionGraph = {
  [YardTaskStatus.CREATED]: {
    [YardTaskStatus.CANCELLED]: [isCheckTask],
    [YardTaskStatus.IN_PROGRESS]: [isCheckTask],
    [YardTaskStatus.COMPLETED]: [isCheckTask, isNotBlocked]
  },
  [YardTaskStatus.IN_PROGRESS]: {
    [YardTaskStatus.CANCELLED]: [isCheckTask],
    [YardTaskStatus.COMPLETED]: [isCheckTask, isNotBlocked]
  }
};

const YARD_TASK_TARGET_STATUSES = [
  YardTaskStatus.CANCELLED,
  YardTaskStatus.COMPLETED,
  YardTaskStatus.IN_PROGRESS
] as const;

function getTransitionGraphForTask(task: TaskViewData): YardTaskTransitionGraph {
  if (isMoveTask(task)) {
    return YARD_MOVE_TASK_TRANSITION_RULES;
  }

  if (isCheckTask(task)) {
    return YARD_CHECK_TASK_TRANSITION_RULES;
  }

  return {};
}

function getTransitionRequirements(
  task: TaskViewData,
  to: YardTaskStatus
): YardTaskTransitionRequirement[] | undefined {
  const from = task.status as YardTaskStatus;

  return getTransitionGraphForTask(task)[from]?.[to];
}

function isYardTaskTransitionEligible(task: TaskViewData, to: YardTaskStatus): boolean {
  const requirements = getTransitionRequirements(task, to);

  if (!requirements) {
    return false;
  }

  return requirements.every(requirement => requirement(task));
}

export function canCancelYardTask(task?: Maybe<TaskViewData>): boolean {
  if (!task?.id) {
    return false;
  }

  return isYardTaskTransitionEligible(task, YardTaskStatus.CANCELLED);
}

export function canStartYardTask(task?: Maybe<TaskViewData>): boolean {
  if (!task?.id) {
    return false;
  }

  return isYardTaskTransitionEligible(task, YardTaskStatus.IN_PROGRESS);
}

export function canCompleteYardTask(task?: Maybe<TaskViewData>): boolean {
  if (!task?.id) {
    return false;
  }

  return isYardTaskTransitionEligible(task, YardTaskStatus.COMPLETED);
}

export function canTransitionYardTaskTo(
  task: Maybe<TaskViewData>,
  targetStatus?: Maybe<string>
): boolean {
  if (!task?.id || !targetStatus || task.status === targetStatus) {
    return false;
  }

  return isYardTaskTransitionEligible(task, targetStatus as YardTaskStatus);
}

/**
 * When a transition is blocked but the user should receive feedback (e.g. incomplete move
 * complete), returns a structured reason. Otherwise `null` (eligible, or silently blocked).
 */
export function getYardTaskTransitionFeedbackReason(
  task: Maybe<TaskViewData>,
  targetStatus?: Maybe<string>
): YardTaskCompleteBlockReason | null {
  if (!task?.id || !targetStatus || task.status === targetStatus) {
    return null;
  }

  if (canTransitionYardTaskTo(task, targetStatus)) {
    return null;
  }

  if (
    ![YardTaskStatus.COMPLETED, YardTaskStatus.IN_PROGRESS].includes(targetStatus as YardTaskStatus)
  ) {
    return null;
  }

  // Blocked is checked first regardless of target, so reusing this for a refused Start is safe —
  // the move-completeness checks below it only ever run once blocked has already ruled itself out.
  return getYardTaskCompleteBlockReason(task);
}

/**
 * Whether the user may attempt a transition from the UI (menu item enabled, click/drop handled).
 *
 * @returns `true` when the transition is eligible (`canTransitionYardTaskTo`) or blocked with user-facing
 * feedback (`getYardTaskTransitionFeedbackReason`), e.g. Complete on an incomplete move task.
 * `false` when the transition is silently invalid (wrong status, no rule, terminal task).
 */
export function isYardTaskTransitionActionable(
  task: Maybe<TaskViewData>,
  targetStatus?: Maybe<string>
): boolean {
  if (!task) {
    return false;
  }

  if (canTransitionYardTaskTo(task, targetStatus)) {
    return true;
  }

  const feedbackReason = getYardTaskTransitionFeedbackReason(task, targetStatus);

  if (feedbackReason === null) {
    return false;
  }

  /**
   * Every other feedback reason (missing move fields, a blocked check task's Complete) stays
   * clickable so the toast can explain it. A move task blocked on one of OD-4117's five
   * conditions is the one exception — Start/Complete disable outright, leaving Add Flag and
   * Cancel as the only available actions, rather than a toast on an item that looks actionable.
   */
  return !isMoveTask(task) || feedbackReason !== YardTaskCompleteBlockReason.Blocked;
}

export function getYardTaskTransitionTargets(task?: Maybe<TaskViewData>): YardTaskStatus[] {
  if (!task?.id) {
    return [];
  }

  return YARD_TASK_TARGET_STATUSES.filter(
    status => status !== task.status && isYardTaskTransitionEligible(task, status)
  );
}

export function isYardTaskTransitionTarget(status?: Maybe<string>): boolean {
  return YARD_TASK_TARGET_STATUSES.some(targetStatus => targetStatus === status);
}

export function canTransitionYardTask(task?: Maybe<TaskViewData>): boolean {
  return getYardTaskTransitionTargets(task).length > 0;
}
